// @ts-strict-ignore
import type { ReactNode } from 'react';
import React, {
  useCallback, useContext, useMemo, useState,
} from 'react';
import {
  StyledHighlighter,
  StyledIndent,
  StyledLineBreakDivider,
  StyledRuleContent,
  StyledRuleContentContainer,
  StyledRuleLine,
  StyledRuleLineItemContent,
} from './styles';
import { getRuleIndex, ruleInsertionPosition, isThresholdRule } from 'flow/service/rules';
import type { GroupRule, RuleBuilderRule } from 'flow/service/rules';
import RuleNumber from './RuleNumber';
import { RuleBuilderContext } from '../../context';
import type { ConnectDragPreview, ConnectDragSource } from 'react-dnd';
import { useDrop } from 'react-dnd';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { setRuleBuilderHoveredRuleIndex } from 'flow/store/redux/slice';
import {
  selectRuleBuilderHighlightedRuleId,
  selectRuleBuilderHoveredRuleIndex,
  selectRuleBuilderPendingGroupInfo,
  selectRuleBuilderHasPendingGroup,
  selectIsRuleDiffed,
} from 'flow/store/redux/selectors';
import { moveThresholdInStateThunk } from 'flow/store/redux/thunks';
import type { GroupPosition, RuleBuilderDragItem } from '../../types';
import PendingCloseGroupDropdown from './PendingCloseGroup';
import PendingOpenGroupDropdown from './PendingOpenGroup';
import {
  getGroupSelectRule,
  getHasPendingCloseParenthesis,
  getHasPendingOpenParenthesis,
  isInstanceIndexMatch,
} from './helpers';
import GroupTypeSelect from './GroupTypeSelect';
import RuleClause from './RuleClause';
import { ContainerType } from '../../../types';
import { isEditable } from '../../helpers';

type RuleTemplateProps = {
  children: ReactNode;
  rule: RuleBuilderRule;
  groupPosition?: GroupPosition;
  dragOpts?: {
    dragSource: ConnectDragSource;
    dragPreview: ConnectDragPreview;
  };
  endContent?: React.ReactNode;
};

/* Ui component that renders rule lines for Groups & Thresholds */
export default function RuleTemplate({
  children,
  rule,
  groupPosition,
  dragOpts,
  endContent,
}: RuleTemplateProps) {
  const dispatch = useAppDispatch();
  const {
    sourceAction,
    editMode,
    containerType,
    instanceId,
    ruleTree,
    ruleIdToDepthMap,
    ruleIdToIndexMap,
  } = useContext(RuleBuilderContext);
  const highlightedRuleId = useAppSelector(selectRuleBuilderHighlightedRuleId);
  const hoveredRuleIndex = useAppSelector(selectRuleBuilderHoveredRuleIndex);
  const pendingGroupInfo = useAppSelector(selectRuleBuilderPendingGroupInfo);
  const hasPendingGroup = useAppSelector(selectRuleBuilderHasPendingGroup);
  const isRuleDiffed = useAppSelector(selectIsRuleDiffed(rule.id, sourceAction.publicId));

  const [isDragHovered, setIsDragHovered] = useState(false);

  const depth = ruleIdToDepthMap[rule.id];
  const index = getRuleIndex({ ruleIdToIndexMap, rule, groupPosition });

  // Add 2 additional indentation levels for lines after the first one
  const displayDepth = index > 1 ? depth + 1 : depth;

  const isHovered = isInstanceIndexMatch(hoveredRuleIndex, instanceId, index);

  const insertAbovePosition = groupPosition === 'end'
    ? ruleInsertionPosition.lastChild
    : ruleInsertionPosition.before;

  const insertBelowPosition = groupPosition === 'start'
    ? ruleInsertionPosition.firstChild
    : ruleInsertionPosition.after;

  const hasPendingOpenParenthesis = useMemo(() => (
    getHasPendingOpenParenthesis({
      rule,
      ruleTree,
      pendingGroupInfo,
      groupPosition,
    })
  ), [rule, ruleTree, pendingGroupInfo, groupPosition]);

  const hasPendingCloseParenthesis = useMemo(() => (
    getHasPendingCloseParenthesis({
      rule,
      ruleTree,
      pendingGroupInfo,
      groupPosition,
    })
  ), [rule, ruleTree, pendingGroupInfo, groupPosition]);

  const toggleableParentGroup: GroupRule | null = useMemo(() => (
    getGroupSelectRule({ rule, ruleTree, groupPosition })
  ), [rule, ruleTree, groupPosition]);

  const canOpenGroup = isThresholdRule(rule) && !hasPendingGroup;

  const canDrop = useCallback((item: RuleBuilderDragItem) => {
    const isSameRule = item.rule.id === rule.id;
    const hasSameDragSource = item.sourceActionId === sourceAction.publicId;

    return hasSameDragSource && !isSameRule;
  }, [rule.id, sourceAction.publicId]);

  const [, drop] = useDrop(() => ({
    accept: `${sourceAction.publicId}-rule`,
    canDrop: item => canDrop(item),
    hover: item => {
      if (canDrop(item)) {
        setIsDragHovered(true);
      }
    },
    drop: (item: RuleBuilderDragItem) => {
      setIsDragHovered(false);
      dispatch(
        moveThresholdInStateThunk({
          action: sourceAction,
          treeRoot: ruleTree,
          rule: item.rule,
          insertProps: {
            relativelyPositionedRule: rule,
            position: insertBelowPosition,
          },
        }),
      );
    },
  }), [
    canDrop,
    index,
    insertBelowPosition,
    ruleTree,
    rule,
    sourceAction,
  ]);

  const isDrawerOrIsland = [ContainerType.DRAWER, ContainerType.ISLAND]
    .includes(containerType);

  return (
    <>
      <StyledRuleLine
        ref={drop}
        isHovered={isHovered}
        onDragLeave={() => { if (isDragHovered) setIsDragHovered(false); }}
        onMouseEnter={() => {
          if (!isInstanceIndexMatch(hoveredRuleIndex, instanceId, index)) {
            dispatch(setRuleBuilderHoveredRuleIndex({ instanceId, index }));
          }
        }}
      >
        <StyledRuleLineItemContent
          hasEndContent={Boolean(endContent)}
          containerType={containerType}
        >
          {isDrawerOrIsland && (
            <RuleNumber
              index={index}
              dragSource={dragOpts ? dragOpts.dragSource : undefined}
              rule={rule}
              insertPosition={{
                above: insertAbovePosition,
                below: insertBelowPosition,
              }}
            />
          )}
          <StyledHighlighter isHighlighted={isRuleDiffed && isDrawerOrIsland}>
            <StyledIndent indentLevel={displayDepth}>
              &nbsp;
            </StyledIndent>
            {index === 1 && <RuleClause />}
            <StyledRuleContentContainer index={index} editable={isEditable(editMode)}>
              {
                toggleableParentGroup && <GroupTypeSelect rule={toggleableParentGroup} />
              }
              {
              (hasPendingOpenParenthesis || canOpenGroup)
                && (
                  <PendingOpenGroupDropdown
                    rule={rule}
                    hasPendingOpenParenthesis={hasPendingOpenParenthesis}
                  />
                )
              }
              <StyledRuleContent
                isHovered={!isRuleDiffed && isHovered}
                isHighlighted={highlightedRuleId === rule.id}
                ref={dragOpts ? dragOpts.dragPreview : undefined}
              >
                {children}
              </StyledRuleContent>
              {hasPendingCloseParenthesis
                && <PendingCloseGroupDropdown rule={rule} index={index} />}
            </StyledRuleContentContainer>
          </StyledHighlighter>
        </StyledRuleLineItemContent>
        {endContent}
      </StyledRuleLine>
      <StyledLineBreakDivider isTriggered={isDragHovered} />
    </>
  );
}
